import React, { useState } from 'react';
import Form from '../../components/common/Form';
import FormSection from '../../components/common/FormSection';
import FormField from '../../components/common/FormField';
import FileUpload from '../common/FileUpload';
import ImageLink from '../common/ImageLink';
import { QuestionDTO, QuestionOptionDTO } from '../../services/apiClient';
import handleChange from '../../utils/formUtils/handleChange';
import {
  useFormValidation,
  ValidationRules,
} from '../../hooks/useFormValidation';
import { useFileApi } from '../../hooks/useFileApi';
import AddNewButton from '../common/AddButton';
import QuestionOptionTable from '../tables/QuestionOptionTable';
import Drawer from '../../components/common/Drawer';
import { confirmDelete } from '../../utils/sweetAlertUtils';
import Button from '../common/Button';
import SimpleAlert from '../common/SimpleAlert';
import { FileTypeConstants } from '../../constants/FileTypeConstants';

interface QuestionOption extends QuestionOptionDTO {
  arrayIndex?: number;
}

interface QuestionFormProps {
  initialValues: QuestionDTO;
  onSubmit: (data: QuestionDTO) => void;
  onCancel: () => void;
}

const FORM_SECTIONS = {
  basicInfo: {
    id: 'basic-info',
    title: 'Basic Information',
  },
  settings: {
    id: 'settings',
    title: 'Settings',
  },
  options: {
    id: 'options',
    title: 'Options',
  },
} as const;

const defaultQuestionOption: QuestionOptionDTO = {
  title: '',
  isCorrectOption: false,
  questionId: undefined,
};

const QuestionForm: React.FC<QuestionFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
}) => {
  const [formData, setFormData] = useState<QuestionDTO>(initialValues);
  const [questionOption, setQuestionOption] = useState<QuestionOption>(
    defaultQuestionOption,
  );
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const { uploadFile } = useFileApi();
  const [uploadingFiles, setUploadingFiles] = useState<{
    [key: string]: boolean;
  }>({});

  const validationRules: ValidationRules = {
    title: [{ required: true, message: 'Question is required' }],
    questionOptions: [
      {
        required: true,
        message: 'At least 2 options are required',
      },
      {
        custom: (options: QuestionOptionDTO[]) => {
          if (!options || options.length < 2) {
            return false;
          }
          const hasCorrectOption = options.some(
            (option) => option.isCorrectOption,
          );
          return hasCorrectOption;
        },
        message:
          'At least 2 options are required and one must be marked as correct',
      },
    ],
  };

  const { errors, validate } = useFormValidation<QuestionDTO>(validationRules);

  const handleSave = () => {
    const formDataToValidate = {
      ...formData,
      questionOptions: formData.questionOptions || [],
    };

    if (validate(formDataToValidate)) {
      onSubmit(formData);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        setUploadingFiles((prev) => ({ ...prev, imageId: true }));
        const response = await uploadFile(
          'files',
          file.name,
          file,
          'questions'
        );
        if (response && response.success && response.data) {
          setFormData(prev => ({
            ...prev,
            imageId: response.data  // Update to use imageId
          }));
        }
      } catch (error) {
        console.error('Error handling file:', error);
      } finally {
        setUploadingFiles((prev) => ({ ...prev, imageId: false }));
      }
    }
  };

  const handleOptionSave = () => {
    if (questionOption) {
      setFormData((prev) => {
        const existingOptions = prev.questionOptions as QuestionOption[] || [];

        let existingIndex = -1;

        if (questionOption.id) {
          existingIndex = existingOptions.findIndex(
            (item) => item.id === questionOption.id,
          );
        } else if (questionOption.arrayIndex !== undefined) {
          existingIndex = existingOptions.findIndex(
            (item) => item.arrayIndex === questionOption.arrayIndex,
          );
        }

        if (existingIndex !== -1) {
          // Editing existing option
          console.log('Editing: ', questionOption);
          return {
            ...prev,
            questionOptions: existingOptions.map((option, index) => {
              if (index === existingIndex) {
                return questionOption;
              }
              if (questionOption.isCorrectOption && index !== existingIndex) {
                return { ...option, isCorrectOption: false };
              }
              return option;
            }),
          };
        } else {
          // Adding new option - assign arrayIndex
          console.log('Adding: ', questionOption);
          const newArrayIndex =
            Math.max(...existingOptions.map((opt) => opt.arrayIndex || 0), 0) +
            1;
          const newQuestionOption = {
            ...questionOption,
            arrayIndex: newArrayIndex,
          };

          const newOptions = questionOption.isCorrectOption
            ? existingOptions.map((option) => ({
              ...option,
              isCorrectOption: false,
            }))
            : existingOptions;

          return {
            ...prev,
            questionOptions: [...newOptions, newQuestionOption],
          };
        }
      });

      handleDrawerClose();
    }
  };

  const handleOptionEdit = (optionToEdit: QuestionOption) => {
    setQuestionOption(optionToEdit);
    setIsDrawerOpen(true);
  };

  const handleOptionDelete = async (optionToDelete: QuestionOptionDTO) => {
    const confirmed = await confirmDelete();
    if (!confirmed) return;

    setFormData((prev) => ({
      ...prev,
      questionOptions:
        prev.questionOptions?.filter(
          (option) => option.id !== optionToDelete.id,
        ) || [],
    }));
  };

  const handleDrawerClose = () => {
    setQuestionOption(defaultQuestionOption);
    setIsDrawerOpen(false);
  };

  const sections = Object.values(FORM_SECTIONS);

  return (
    <Form sections={sections} onSave={handleSave} onCancel={onCancel}>
      <FormSection
        id={FORM_SECTIONS.basicInfo.id}
        title={FORM_SECTIONS.basicInfo.title}
      >
        <FormField
          id="title"
          label="Question"
          type="textarea"
          rows={4}
          value={formData.title || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Title"
          required={true}
          error={errors.title}
        />

        {formData.imageId && (
          <ImageLink
            fileId={formData.imageId}
            label="Question Image"
          />
        )}
        <FileUpload
          id="imageUpload"
          onChange={handleFileChange}
          title="Upload Question Image"
          accept={FileTypeConstants.ALL_SUPPORTED}
          required={false}
          error={errors.imageId}  // Update error field name
          isUploading={uploadingFiles['imageId']}
        />
      </FormSection>
      <FormSection
        id={FORM_SECTIONS.settings.id}
        title={FORM_SECTIONS.settings.title}
      >
        <FormField
          id="isActive"
          label="Active"
          type="checkbox"
          value={formData.isActive || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Is Active"
          required={false}
          error={errors.isActive}
          helper="Check to make record active"
        />
        <FormField
          id="rotateOptions"
          label="Rotate Options"
          type="checkbox"
          value={formData.rotateOptions || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Rotate Options"
          required={false}
          error={errors.rotateOptions}
          helper="Check to rotate question options"
        />
      </FormSection>
      <FormSection
        id={FORM_SECTIONS.options.id}
        title={FORM_SECTIONS.options.title}
        subtitle="Manage the different options available for this question. You can add, edit, or delete options as needed."
        headerActions={
          <AddNewButton
            variant="secondary"
            label="Add New Option"
            onClick={() => setIsDrawerOpen(true)}
          />
        }
      >
        <div className="mb-4">
          <SimpleAlert
            variant="warning"
            message="This question requires at least 2 options, with one option marked as the correct answer."
          />
          {errors.questionOptions && (
            <SimpleAlert variant="error" message={errors.questionOptions} />
          )}
        </div>
        <QuestionOptionTable
          data={formData.questionOptions || []}
          handleEdit={(dto) => handleOptionEdit(dto)}
          handleDelete={(dto) => handleOptionDelete(dto)}
          currentPage={currentPage}
          totalPages={Math.ceil(
            (formData.questionOptions?.length || 0) / pageSize,
          )}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          isLoading={false}
        />

        <Drawer
          isOpen={isDrawerOpen}
          onClose={handleDrawerClose}
          title={questionOption.id ? 'Edit Option' : 'Add New Option'}
        >
          {isDrawerOpen && (
            <div className="space-y-4">
              <FormField
                id="title"
                label="Option Text"
                type="textarea"
                rows={4}
                value={questionOption.title || ''}
                onChange={(e) => handleChange(e, setQuestionOption)}
                placeholder="Enter option text"
                required={true}
              />

              <FormField
                id="isCorrectOption"
                label="Correct Answer"
                type="checkbox"
                value={questionOption.isCorrectOption || false}
                onChange={(e) => handleChange(e, setQuestionOption)}
                placeholder="Is this the correct answer?"
                required={false}
                helper="Check if this is the correct answer"
              />

              {!formData.rotateOptions && (
                <FormField
                  id="displayOrder"
                  label="Display Order"
                  type="number"
                  value={questionOption.displayOrder ?? ''}
                  onChange={(e) => handleChange(e, setQuestionOption)}
                  placeholder="Enter display order (e.g., 0, 1, 2...)"
                  required={false}
                  helper="Specify the order in which this option should appear (lower numbers appear first)"
                />
              )}

              <div className="flex justify-end space-x-4">
                <Button variant="secondary" onClick={handleDrawerClose}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={handleOptionSave}
                  disabled={!questionOption.title}
                >
                  {questionOption.id ? 'Save Changes' : 'Add Option'}
                </Button>
              </div>
            </div>
          )}
        </Drawer>
      </FormSection>
    </Form>
  );
};

export default QuestionForm;
